import { useEffect, useState } from "react";
import AuthService from "../../../api/services/AuthService";

function hasSession() {
  try {
    return !!(localStorage.getItem("token") || sessionStorage.getItem("token"));
  } catch {
    return false;
  }
}

/** Spot fees are percents: 0.1 → 0.100%, 0.13 → 0.130%. */
function formatTradeFeePercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const four = n.toFixed(4);
  return `${four.endsWith("0") ? n.toFixed(3) : four}%`;
}

/** Futures fees are fractions: 0.0002 → 0.02%, 0.0005 → 0.05%. */
function formatFractionFeePercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const text = (n * 100).toFixed(8).replace(/\.?0+$/, "");
  return `${text || "0"}%`;
}

function pairFeeLabel(fee) {
  if (fee == null || fee === "") return "---";
  return `${fee}%`;
}

/**
 * `product` is `spot` (also isolated and cross), `futures`, or `options`.
 * `fees` is null unless fee_source is vip, so Regular keeps the pair, contract, or options config rate.
 * `ready` stays false until that check finishes, so the form does not flash a placeholder rate.
 */
export function useVipTradeFees(product = "spot") {
  const [state, setState] = useState(() => ({ ready: !hasSession(), fees: null }));

  useEffect(() => {
    if (!hasSession()) return undefined;
    let cancelled = false;
    const requested = product === "futures" || product === "options" ? product : "spot";
    const formatFee = requested === "futures" ? formatFractionFeePercent : formatTradeFeePercent;
    AuthService.getMyVip(requested)
      .then((res) => {
        if (cancelled) return;
        const data = res?.success && res.data && !Array.isArray(res.data) ? res.data : null;
        if (!data || data.fee_source !== "vip") {
          setState({ ready: true, fees: null });
          return;
        }
        const maker = formatFee(data.maker_fee);
        const taker = formatFee(data.taker_fee);
        setState({
          ready: true,
          fees: maker && taker ? { maker, taker } : null,
        });
      })
      .catch(() => {
        if (!cancelled) setState({ ready: true, fees: null });
      });
    return () => {
      cancelled = true;
    };
  }, [product]);

  return state;
}

/** VIP rate when the account has one. Otherwise the pair or contract rate. "---" until that is known. */
export function resolveTradeFeeLabel(vipState, pairFee, side, asFraction = false) {
  if (!vipState?.ready) return "---";
  const vipText = side === "taker" ? vipState.fees?.taker : vipState.fees?.maker;
  if (vipText) return vipText;
  if (asFraction) return formatFractionFeePercent(pairFee) || "---";
  return pairFeeLabel(pairFee);
}
